import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkle, MagnifyingGlass } from "@phosphor-icons/react";
import { VoiceDictationButton } from "@/components/VoiceDictationButton";
import { FileUploadZone } from "@/components/FileUploadZone";
import { StrategyTemplatePickerModal } from "@/components/StrategyTemplatePickerModal";

export function TopicResearchSection({
  topic, setTopic,
  strategyProfileId, setStrategyProfileId,
  strategyProfiles, defaultStrategyProfileId,
  showTemplatePicker, setShowTemplatePicker,
  onStrategyCloned,
  researchText, setResearchText,
  researching, onResearch,
  files, setFiles, uploadedFileIds,
}) {
  return (
    <div className="space-y-6">
      <div>
        <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
          Topic <span className="text-[#FF4400]">*</span>
        </label>
        <div className="relative">
          <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="What is this content about?" maxLength={255} required type="text" inputMode="text" className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] h-11 pr-10" data-testid="input-topic" />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <VoiceDictationButton onTranscript={(text) => setTopic((prev) => prev ? `${prev} ${text}` : text)} className="w-7 h-7" />
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">Strategy Profile</label>
          <button type="button" onClick={() => setShowTemplatePicker(true)} className="text-[11px] text-[#FF4400] hover:text-[#FF6633] font-medium transition-colors" data-testid="start-from-template-btn">Start from template</button>
        </div>
        <Select value={strategyProfileId || "__none__"} onValueChange={(val) => setStrategyProfileId(val === "__none__" ? "" : val)}>
          <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="select-strategy-profile">
            <SelectValue placeholder="Use default business strategy" />
          </SelectTrigger>
          <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
            <SelectItem value="__none__" className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">Use default business strategy</SelectItem>
            {strategyProfiles.map((sp) => (
              <SelectItem key={sp.strategy_profile_id} value={sp.strategy_profile_id} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">
                {sp.name}{sp.strategy_profile_id === defaultStrategyProfileId ? " (Default)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {strategyProfiles.length === 0 && (
          <p className="text-[10px] text-[var(--text-muted)] mt-1.5">
            No profiles yet. <button type="button" onClick={() => setShowTemplatePicker(true)} className="text-[#FF4400] hover:underline">Browse templates</button> or <Link to="/strategy/templates" className="text-[#FF4400] hover:underline">view all</Link>.
          </p>
        )}
        <StrategyTemplatePickerModal isOpen={showTemplatePicker} onClose={() => setShowTemplatePicker(false)} onCloned={onStrategyCloned} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)]">Research / Source Material <span className="text-[#FF4400]">*</span></label>
            <p className="text-xs text-[var(--text-muted)] mt-1">Paste text, upload files, or both. All content is combined as research context for the AI.</p>
          </div>
        </div>
        <button type="button" disabled={researching || !topic.trim()} onClick={onResearch} className="w-full mb-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-[var(--bg-card)] border border-dashed border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[#FF4400]/50 hover:text-[#FF4400] transition-all disabled:opacity-40 disabled:cursor-not-allowed" data-testid="research-topic-btn">
          {researching ? (<><Sparkle size={16} className="animate-spin" /> Researching with Perplexity&hellip;</>) : (<><MagnifyingGlass size={16} /> Research this topic with AI</>)}
        </button>
        <Textarea value={researchText} onChange={(e) => setResearchText(e.target.value)} placeholder="Paste research, notes, data, client context, raw thoughts, quotes, stats, or any source material here..." maxLength={50000} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] focus:border-[#FF4400] min-h-[200px] resize-y" data-testid="input-research" />
        <div className="mt-3">
          <FileUploadZone files={files} onFilesChange={setFiles} uploadedFileIds={uploadedFileIds} />
        </div>
      </div>
    </div>
  );
}
